/**
 * moonshot-locker-terms.js
 *
 * Captures the customer's first-time acknowledgment of the Locker
 * terms (no refunds + items forfeited after locker_max_hold_days).
 * Intercepts clicks on the "Send to my Locker" PDP radio:
 *
 *   - If the customer has already accepted (cached or fetched from the
 *     Worker /customer/locker/terms-acceptance endpoint), no modal
 *     fires — the radio just selects.
 *   - If not, an inline modal pops asking them to agree. On "I agree",
 *     we POST to the Worker, cache locally, then complete the radio
 *     selection. On "Cancel", we revert the radio to whatever was
 *     previously selected.
 *
 * Loaded after moonshot-stock-widget.js via layout/theme.liquid.
 */
(function () {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  const WORKER_PATH = "/apps/moonshot/locker/terms-acceptance";
  const STYLE_ID = "moonshot-locker-terms-styles";
  const MODAL_ID = "moonshot-locker-terms-modal";
  // Bump this string whenever the modal copy changes materially — customers
  // who previously accepted an older version will be re-prompted. Pair with
  // the Worker-side locker_terms_accepted_version metafield.
  const TERMS_VERSION = "v4-2026-05-20-moonshot-locker";
  const CACHE_KEY = "moonshot_locker_terms_accepted_at";
  const CACHE_VERSION_KEY = "moonshot_locker_terms_accepted_version";

  // ─── Acceptance state (cached on window after first fetch) ──────────
  let acceptancePromise = null;
  function readCachedAcceptance() {
    try {
      const at = localStorage.getItem(CACHE_KEY);
      const ver = localStorage.getItem(CACHE_VERSION_KEY);
      return at && ver === TERMS_VERSION ? { at, ver } : null;
    } catch (e) {
      return null;
    }
  }
  function writeCachedAcceptance(iso, version) {
    try {
      localStorage.setItem(CACHE_KEY, iso);
      localStorage.setItem(CACHE_VERSION_KEY, version || TERMS_VERSION);
    } catch (e) {
      /* ignore */
    }
  }
  function clearCachedAcceptance() {
    try {
      localStorage.removeItem(CACHE_KEY);
      localStorage.removeItem(CACHE_VERSION_KEY);
    } catch (e) {
      /* ignore */
    }
  }

  async function fetchAcceptance() {
    if (acceptancePromise) return acceptancePromise;
    const local = readCachedAcceptance();
    if (local) {
      acceptancePromise = Promise.resolve({ hasAccepted: true, acceptedAt: local.at });
      return acceptancePromise;
    }
    acceptancePromise = (async () => {
      try {
        const res = await fetch(WORKER_PATH, {
          method: "GET",
          headers: { Accept: "application/json" },
          credentials: "include",
        });
        if (!res.ok) return { hasAccepted: false, acceptedAt: null };
        const data = await res.json();
        if (data && data.hasAccepted && data.acceptedAt) {
          // If the customer accepted an older version of the terms,
          // treat as NOT accepted so the modal re-prompts. Clear the
          // stale cache so we don't paper over it locally.
          if (data.acceptedVersion && data.acceptedVersion !== TERMS_VERSION) {
            clearCachedAcceptance();
            return { hasAccepted: false, acceptedAt: null, staleVersion: data.acceptedVersion };
          }
          writeCachedAcceptance(data.acceptedAt, data.acceptedVersion || TERMS_VERSION);
          return { hasAccepted: true, acceptedAt: data.acceptedAt };
        }
        return { hasAccepted: false, acceptedAt: null };
      } catch (e) {
        return { hasAccepted: false, acceptedAt: null };
      }
    })();
    return acceptancePromise;
  }

  async function recordAcceptance() {
    try {
      const res = await fetch(WORKER_PATH, {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ version: TERMS_VERSION }),
      });
      if (!res.ok) throw new Error("HTTP " + res.status);
      const data = await res.json();
      if (data && data.acceptedAt) {
        writeCachedAcceptance(data.acceptedAt, data.acceptedVersion || TERMS_VERSION);
        acceptancePromise = Promise.resolve({ hasAccepted: true, acceptedAt: data.acceptedAt });
        return true;
      }
      throw new Error("Worker response missing acceptedAt");
    } catch (e) {
      console.error("[moonshot-locker-terms] record failed:", e);
      return false;
    }
  }

  // ─── Modal UI ───────────────────────────────────────────────────────
  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = `
      #${MODAL_ID}-overlay {
        position: fixed; inset: 0;
        background: rgba(0,0,0,0.55);
        z-index: 100100;
        display: flex; align-items: center; justify-content: center;
        padding: 20px;
        overflow-y: auto;  /* Lets the overlay scroll when modal taller than viewport */
        animation: moonshot-locker-fade-in 0.18s ease-out;
        /* Use dvh fallback for iOS mobile address-bar transitions —
           prevents the modal getting clipped under the browser chrome. */
        max-height: 100vh;
        max-height: 100dvh;
      }
      @keyframes moonshot-locker-fade-in {
        from { opacity: 0; }
        to   { opacity: 1; }
      }
      #${MODAL_ID} {
        background: #ffffff;
        border-radius: 12px;
        max-width: 520px; width: 100%;
        /* Constrain to viewport and use flex-column so the body
           scrolls independently while the action buttons stay
           always-visible at the bottom. */
        max-height: calc(100vh - 40px);
        max-height: calc(100dvh - 40px);
        display: flex; flex-direction: column;
        box-shadow: 0 20px 60px rgba(0,0,0,0.35);
        font-family: inherit;
        overflow: hidden;  /* clip rounded corners against the scrolling body */
      }
      #${MODAL_ID} .moonshot-locker-modal__body {
        padding: 24px 24px 8px;
        overflow-y: auto;
        flex: 1 1 auto;
        -webkit-overflow-scrolling: touch;  /* momentum scroll on iOS */
      }
      #${MODAL_ID} h2 {
        margin: 0 0 10px 0;
        font-size: 1.25rem;
        font-weight: 700;
        color: #1d1f2b;
      }
      #${MODAL_ID} p {
        margin: 0 0 12px 0;
        font-size: 0.95rem;
        line-height: 1.5;
        color: #3a3a3a;
      }
      #${MODAL_ID} ul,
      #${MODAL_ID} ol {
        margin: 0 0 14px 0;
        padding-left: 22px;
        font-size: 0.92rem;
        line-height: 1.55;
        color: #3a3a3a;
      }
      #${MODAL_ID} ul li,
      #${MODAL_ID} ol li {
        margin: 6px 0;
      }
      #${MODAL_ID} ol.moonshot-locker-modal__rules {
        list-style: decimal;
      }
      #${MODAL_ID} ol.moonshot-locker-modal__rules li::marker {
        font-weight: 700;
        color: #1d1f2b;
      }
      #${MODAL_ID} .moonshot-locker-modal__actions {
        flex: 0 0 auto;  /* never compress; always visible */
        display: flex;
        gap: 10px;
        justify-content: flex-end;
        padding: 14px 24px 20px;
        background: #fff;
        border-top: 1px solid #eaeaea;
      }
      @media (max-width: 520px) {
        #${MODAL_ID}-overlay {
          padding: 0;  /* full-bleed modal on small screens */
        }
        #${MODAL_ID} {
          border-radius: 0;
          max-height: 100vh;
          max-height: 100dvh;
        }
        #${MODAL_ID} .moonshot-locker-modal__actions {
          /* Stack buttons on tiny screens so neither is clipped or
             requires a horizontal scroll. */
          flex-direction: column-reverse;
        }
        #${MODAL_ID} .moonshot-locker-modal__actions button {
          width: 100%;
        }
      }
      #${MODAL_ID} button {
        font-family: inherit;
        font-size: 0.95rem;
        font-weight: 600;
        padding: 10px 18px;
        border-radius: 6px;
        cursor: pointer;
        border: none;
      }
      #${MODAL_ID} .moonshot-locker-modal__cancel {
        background: #f0eee6;
        color: #4a4a4a;
      }
      #${MODAL_ID} .moonshot-locker-modal__cancel:hover {
        background: #e3dfd1;
      }
      #${MODAL_ID} .moonshot-locker-modal__accept {
        background: #1d1f2b;
        color: #ffffff;
      }
      #${MODAL_ID} .moonshot-locker-modal__accept:hover {
        background: #2a2d3d;
      }
      #${MODAL_ID} .moonshot-locker-modal__accept[disabled] {
        opacity: 0.6;
        cursor: wait;
      }
    `;
    document.head.appendChild(style);
  }

  function showModal() {
    return new Promise((resolve) => {
      injectStyles();
      const existing = document.getElementById(MODAL_ID + "-overlay");
      if (existing) existing.remove();

      const overlay = document.createElement("div");
      overlay.id = MODAL_ID + "-overlay";
      overlay.setAttribute("role", "dialog");
      overlay.setAttribute("aria-modal", "true");

      const modal = document.createElement("div");
      modal.id = MODAL_ID;

      // Modal is split into a SCROLLABLE body + a FIXED footer with the
      // action buttons. On mobile the body scrolls; the Accept/Cancel
      // row is always visible at the bottom of the viewport.
      modal.innerHTML = `
        <div class="moonshot-locker-modal__body">
          <h2>Welcome to your Moonshot Locker</h2>
          <p>
            Before adding items to your Moonshot Locker, please review the
            four rules below. You'll only see this once — your acceptance
            is logged with a date, time, and IP address on your account.
          </p>
          <ol class="moonshot-locker-modal__rules">
            <li><strong>Final sale.</strong> TCG singles, TCG sealed
                product, KKDs, Moonshot Rips livestream items, and
                Clearance items are non-refundable once added to your
                Moonshot Locker.</li>
            <li><strong>Refund policy.</strong> For all other categories,
                you may request a refund within <strong>14 days of
                purchase</strong>. The clock starts the day the item is
                added to your Moonshot Locker — not when it ships or is
                picked up.</li>
            <li><strong>Length of hold.</strong> Items remain in your
                Moonshot Locker for <strong>up to 90 days</strong>. After
                day 90, the item moves to a 30-day Final-Pickup window at
                your home store — pickup only, no shipping, no further
                extension.</li>
            <li><strong>Forfeiture.</strong> Items left unclaimed at the
                end of the Final-Pickup window (<strong>day 120 from add
                date</strong>) are forfeited and donated or redistributed
                at our discretion, with no refund or credit. We'll send
                multiple email reminders before this happens — at 30, 14,
                and 7 days before your 90-day hold ends, again when the
                Final-Pickup window opens, and at 14 and 7 days before
                forfeit. We do everything reasonable to make sure you
                never lose an item.</li>
          </ol>
          <p style="font-size: 0.92rem; color: #3a3a3a; margin: 14px 0 4px;">
            A Sweep ships your entire Moonshot Locker in one shipment
            (free at $250+). You'll see shipping cost and confirm before
            anything moves.
          </p>
          <p style="font-size: 0.92rem; color: #3a3a3a; margin: 6px 0 4px;">
            You can view your Moonshot Locker contents and pay for
            shipping any time from
            <strong>Your Account → Your Moonshot Locker</strong>.
          </p>
          <p style="font-size: 0.85rem; color: #6a6a6a; margin-top: 14px;">
            <a href="/pages/locker-terms" target="_blank" rel="noopener" style="color: #1d1f2b; text-decoration: underline;">Click here to read the full Moonshot Locker Terms &amp; Policy</a>
          </p>
        </div>
        <div class="moonshot-locker-modal__actions">
          <button type="button" class="moonshot-locker-modal__cancel">Cancel</button>
          <button type="button" class="moonshot-locker-modal__accept">I understand and agree</button>
        </div>
      `;
      overlay.appendChild(modal);
      document.body.appendChild(overlay);

      const acceptBtn = modal.querySelector(".moonshot-locker-modal__accept");
      const cancelBtn = modal.querySelector(".moonshot-locker-modal__cancel");

      acceptBtn.addEventListener("click", async () => {
        acceptBtn.disabled = true;
        acceptBtn.textContent = "Saving…";
        const ok = await recordAcceptance();
        overlay.remove();
        resolve(ok);
      });
      cancelBtn.addEventListener("click", () => {
        overlay.remove();
        resolve(false);
      });
      // Click outside the modal cancels (same as cancel button).
      overlay.addEventListener("click", (e) => {
        if (e.target === overlay) {
          overlay.remove();
          resolve(false);
        }
      });
      // ESC also cancels.
      function escHandler(e) {
        if (e.key === "Escape") {
          overlay.remove();
          document.removeEventListener("keydown", escHandler);
          resolve(false);
        }
      }
      document.addEventListener("keydown", escHandler);
    });
  }

  // ─── Click interceptor on the Locker radio ──────────────────────────
  // Capture phase so we run BEFORE any other listeners. If the
  // customer hasn't accepted, prevent the radio from being checked
  // until they go through the modal.
  document.addEventListener(
    "click",
    async function (e) {
      const t = e.target;
      if (!t || !t.matches) return;
      if (!t.matches('[data-moonshot-fulfillment-input][data-method="locker"]')) {
        return;
      }
      // Check acceptance state. If already accepted, let the radio
      // do its normal thing (no interception).
      const state = await fetchAcceptance();
      if (state && state.hasAccepted) return;

      // Not accepted yet — block the selection and show modal.
      e.preventDefault();
      e.stopPropagation();
      // Record what was checked before so we can revert on cancel.
      const fs = t.closest("[data-moonshot-fulfillment]");
      const previouslyChecked = fs
        ? fs.querySelector("[data-moonshot-fulfillment-input]:checked")
        : null;
      const previousValue = previouslyChecked ? previouslyChecked.value : null;

      const accepted = await showModal();
      if (accepted) {
        // Now actually select the Locker radio.
        t.checked = true;
        t.dispatchEvent(new Event("change", { bubbles: true }));
      } else {
        // Restore the previous selection (or leave nothing checked
        // if there was no previous selection).
        if (previousValue) {
          const restore = fs.querySelector(
            '[data-moonshot-fulfillment-input][value="' + previousValue.replace(/"/g, '\\"') + '"]',
          );
          if (restore) {
            restore.checked = true;
            restore.dispatchEvent(new Event("change", { bubbles: true }));
          }
        }
      }
    },
    true,
  );

  // Pre-warm the acceptance check on page load so the first Locker
  // click is instant. Non-blocking.
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", fetchAcceptance);
  } else {
    fetchAcceptance();
  }

  // ─── Express checkout buttons: hide when Locker/stream involved ────
  // (B — 2026-05-29, extended 2026-06-01) Shop Pay's express checkout
  // UI is Shopify-controlled and shows only Ship / Pickup tabs. It
  // can't represent the Locker option or the stream-pack timing rules,
  // and a customer who hits it for those carts either drops their
  // Locker selection silently or trips Shopify's "No Locations in
  // United States with your items" error (reported 2026-06-01 on the
  // WAR: 4 Pack Bundle stream item).
  //
  // We hide ALL dynamic payment buttons (.shopify-payment-button +
  // .additional-checkout-buttons, the cart drawer / /cart equivalent)
  // when ANY of these are true:
  //   1. Locker radio is selected on this PDP
  //   2. The cart already contains a line with properties[fulfillment]=locker
  //   3. The current product is stream-only (any fulfillment combo on a
  //      stream item carries timing constraints Shop Pay can't express)
  //
  // Otherwise (plain ship/pickup, non-stream, non-locker carts) the
  // express buttons remain visible — no regression for the common case.
  let cachedCartHasLocker = false;

  async function refreshCartLockerState() {
    try {
      const res = await fetch("/cart.js", {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      });
      if (!res.ok) {
        cachedCartHasLocker = false;
        return;
      }
      const cart = await res.json();
      const items = (cart && cart.items) || [];
      cachedCartHasLocker = items.some(function (item) {
        const props = item.properties || {};
        return props.fulfillment === "locker";
      });
    } catch (e) {
      cachedCartHasLocker = false;
    }
    applyPaymentButtonVisibility();
  }

  function applyPaymentButtonVisibility() {
    const selectorRoots = document.querySelectorAll(
      "[data-moonshot-fulfillment]",
    );
    // Determine the three signals.
    let lockerSelected = false;
    let streamOnlyOnPage = false;
    for (const fs of selectorRoots) {
      if (fs.getAttribute("data-is-stream-only") === "true") {
        streamOnlyOnPage = true;
      }
      const checked = fs.querySelector(
        "[data-moonshot-fulfillment-input]:checked",
      );
      if (checked && checked.getAttribute("data-method") === "locker") {
        lockerSelected = true;
      }
    }
    const shouldHide = lockerSelected || streamOnlyOnPage || cachedCartHasLocker;

    // Hide both the PDP-level shopify-payment-button container AND the
    // cart-level additional-checkout-buttons container (used by drawer
    // and /cart). Mark hides with our data-attr so we only restore the
    // ones we hid.
    const containers = document.querySelectorAll(
      ".shopify-payment-button, .additional-checkout-buttons",
    );
    for (const el of containers) {
      if (shouldHide) {
        el.setAttribute("data-moonshot-locker-hide", "1");
        el.style.display = "none";
      } else if (el.getAttribute("data-moonshot-locker-hide") === "1") {
        el.removeAttribute("data-moonshot-locker-hide");
        el.style.display = "";
      }
    }
  }

  // Re-evaluate on every fulfillment radio change.
  document.addEventListener(
    "change",
    function (e) {
      const t = e.target;
      if (!t || !t.matches) return;
      if (!t.matches("[data-moonshot-fulfillment-input]")) return;
      applyPaymentButtonVisibility();
    },
    false,
  );
  // Re-evaluate after any cart update — handles Add-to-Cart from PDP,
  // qty changes in drawer, removals, etc. Theme dispatches cart:refresh
  // on both document and documentElement (see assets/theme.js + custom.js).
  document.addEventListener("cart:refresh", refreshCartLockerState);
  document.documentElement.addEventListener("cart:refresh", refreshCartLockerState);
  document.addEventListener("cart:updated", refreshCartLockerState);
  document.addEventListener("cart:rerendered", refreshCartLockerState);

  // Initial pass: read cart state + run the visibility check.
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      applyPaymentButtonVisibility();
      refreshCartLockerState();
    });
  } else {
    applyPaymentButtonVisibility();
    refreshCartLockerState();
  }

  // ─── Locker "?" tooltip toggle ─────────────────────────────────────
  // The trigger lives inside the radio's <label>, so default click
  // behavior would flip the radio. preventDefault + stopPropagation
  // keep this strictly informational. Click again (or outside) to close.
  document.addEventListener(
    "click",
    function (e) {
      const t = e.target;
      if (!t || !t.closest) return;

      const trigger = t.closest("[data-moonshot-locker-tip]");
      if (trigger) {
        e.preventDefault();
        e.stopPropagation();
        const wrapper = trigger.closest(".moonshot-fulfillment__label");
        const panel = wrapper && wrapper.querySelector("[data-moonshot-locker-tip-panel]");
        if (!panel) return;
        const opening = panel.hidden;
        // Close any other open tooltip panels first.
        document
          .querySelectorAll("[data-moonshot-locker-tip-panel]")
          .forEach(function (p) { p.hidden = true; });
        document
          .querySelectorAll("[data-moonshot-locker-tip]")
          .forEach(function (b) { b.setAttribute("aria-expanded", "false"); });
        panel.hidden = !opening;
        trigger.setAttribute("aria-expanded", opening ? "true" : "false");
        return;
      }

      // Click outside any panel — close all.
      if (!t.closest("[data-moonshot-locker-tip-panel]")) {
        document
          .querySelectorAll("[data-moonshot-locker-tip-panel]")
          .forEach(function (p) { p.hidden = true; });
        document
          .querySelectorAll("[data-moonshot-locker-tip]")
          .forEach(function (b) { b.setAttribute("aria-expanded", "false"); });
      }
    },
    true,
  );
})();
